import { StringsKeys } from "@/core/internationalization/strings";
import Exception from "@/core/exceptions/Exception";
import ExceptionService from "@/core/exceptions/exceptionService/ExceptionService";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import { CustomerKYCData } from "@/core/customers/entities/CustomerKYC";
import { CustomerKYCUpdateDTO } from "@/core/customers/entities/CustomerKYCUpdate";
import type { ContactField, FicheClient360State, FetchResult } from "./types";

type MergeFieldApplier = (
  nextKyc: CustomerKYCData,
  updates: Partial<CustomerKYCUpdateDTO>,
) => void;

function applyMergeField(
  value: unknown,
  apply: (normalized: unknown) => void,
  normalize: (value: unknown) => unknown = (currentValue) => currentValue,
): void {
  if (value !== undefined) {
    apply(normalize(value));
  }
}

const DIRECT_MERGE_FIELDS: readonly MergeFieldApplier[] = [
  (nextKyc, updates) => {
    applyMergeField(updates.phone1, (value) => {
      nextKyc.phone1 = value as string;
    });
  },
  (nextKyc, updates) => {
    applyMergeField(updates.email1, (value) => {
      nextKyc.email1 = value as string;
    });
  },
];

const NULL_FALLBACK_MERGE_FIELDS: readonly MergeFieldApplier[] = [
  (nextKyc, updates) =>
    applyMergeField(
      updates.phone2,
      (value) => {
        nextKyc.phone2 = value as CustomerKYCData["phone2"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.phone3,
      (value) => {
        nextKyc.phone3 = value as CustomerKYCData["phone3"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.phone4,
      (value) => {
        nextKyc.phone4 = value as CustomerKYCData["phone4"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.phone1CountryCode,
      (value) => {
        nextKyc.phone1CountryCode = value as CustomerKYCData["phone1CountryCode"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.phone2CountryCode,
      (value) => {
        nextKyc.phone2CountryCode = value as CustomerKYCData["phone2CountryCode"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.phone3CountryCode,
      (value) => {
        nextKyc.phone3CountryCode = value as CustomerKYCData["phone3CountryCode"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.phone4CountryCode,
      (value) => {
        nextKyc.phone4CountryCode = value as CustomerKYCData["phone4CountryCode"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.email2,
      (value) => {
        nextKyc.email2 = value as CustomerKYCData["email2"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.fax,
      (value) => {
        nextKyc.fax = value as CustomerKYCData["fax"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.resident,
      (value) => {
        nextKyc.resident = value as CustomerKYCData["resident"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.fatherLastName,
      (value) => {
        nextKyc.fatherLastName = value as CustomerKYCData["fatherLastName"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.motherMaidenName,
      (value) => {
        nextKyc.motherMaidenName = value as CustomerKYCData["motherMaidenName"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.fatherFirstNames,
      (value) => {
        nextKyc.fatherFirstNames = value as CustomerKYCData["fatherFirstNames"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.motherFirstNames,
      (value) => {
        nextKyc.motherFirstNames = value as CustomerKYCData["motherFirstNames"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.deathDate,
      (value) => {
        nextKyc.deathDate = value as CustomerKYCData["deathDate"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.profession,
      (value) => {
        nextKyc.profession = value as CustomerKYCData["profession"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.sectorActivity,
      (value) => {
        nextKyc.sectorActivity = value as CustomerKYCData["sectorActivity"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.monthlyIncome,
      (value) => {
        nextKyc.monthlyIncome = value as CustomerKYCData["monthlyIncome"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.qualityCode,
      (value) => {
        nextKyc.qualityCode = value as CustomerKYCData["qualityCode"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.spouseLastName,
      (value) => {
        nextKyc.spouseLastName = value as CustomerKYCData["spouseLastName"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.spouseFirstName,
      (value) => {
        nextKyc.spouseFirstName = value as CustomerKYCData["spouseFirstName"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.numberChildren,
      (value) => {
        nextKyc.numberChildren = value as CustomerKYCData["numberChildren"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.relationOtherFinancialInstitutions,
      (value) => {
        nextKyc.relationOtherFinancialInstitutions =
          value as CustomerKYCData["relationOtherFinancialInstitutions"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.bankEmployee,
      (value) => {
        nextKyc.bankEmployee = value as CustomerKYCData["bankEmployee"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.patrimoineRealEstateCount,
      (value) => {
        nextKyc.patrimoineRealEstateCount =
          value as CustomerKYCData["patrimoineRealEstateCount"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.patrimoineRealEstateAssets,
      (value) => {
        nextKyc.patrimoineRealEstateAssets =
          value as CustomerKYCData["patrimoineRealEstateAssets"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.patrimoineRealEstateInsured,
      (value) => {
        nextKyc.patrimoineRealEstateInsured =
          value as CustomerKYCData["patrimoineRealEstateInsured"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.patrimoineRealEstateEstimatedValue,
      (value) => {
        nextKyc.patrimoineRealEstateEstimatedValue =
          value as CustomerKYCData["patrimoineRealEstateEstimatedValue"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.patrimoineOtherBankAccountsValue,
      (value) => {
        nextKyc.patrimoineOtherBankAccountsValue =
          value as CustomerKYCData["patrimoineOtherBankAccountsValue"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.patrimoineOtherBankAccounts,
      (value) => {
        nextKyc.patrimoineOtherBankAccounts =
          value as CustomerKYCData["patrimoineOtherBankAccounts"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.patrimoineValuableObjects,
      (value) => {
        nextKyc.patrimoineValuableObjects =
          value as CustomerKYCData["patrimoineValuableObjects"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.patrimoineValuablesInsured,
      (value) => {
        nextKyc.patrimoineValuablesInsured =
          value as CustomerKYCData["patrimoineValuablesInsured"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.patrimoineValuablesValue,
      (value) => {
        nextKyc.patrimoineValuablesValue =
          value as CustomerKYCData["patrimoineValuablesValue"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.situationPatrimoniale,
      (value) => {
        nextKyc.situationPatrimoniale =
          value as CustomerKYCData["situationPatrimoniale"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.sourceDesFonds,
      (value) => {
        nextKyc.sourceDesFonds = value as CustomerKYCData["sourceDesFonds"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.objetRelationBancaire,
      (value) => {
        nextKyc.objetRelationBancaire =
          value as CustomerKYCData["objetRelationBancaire"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.residenceFiscalePays,
      (value) => {
        nextKyc.residenceFiscalePays =
          value as CustomerKYCData["residenceFiscalePays"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.ppe,
      (value) => {
        nextKyc.ppe = value as CustomerKYCData["ppe"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.fonctionPpeExercee,
      (value) => {
        nextKyc.fonctionPpeExercee =
          value as CustomerKYCData["fonctionPpeExercee"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.beneficiaireEffectif,
      (value) => {
        nextKyc.beneficiaireEffectif =
          value as CustomerKYCData["beneficiaireEffectif"];
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.identiteBeneficiaireEffectif,
      (value) => {
        nextKyc.identiteBeneficiaireEffectif =
          value as CustomerKYCData["identiteBeneficiaireEffectif"];
      },
      (value) => value ?? null,
    ),
];

const ADDRESS_NULL_FALLBACK_FIELDS: readonly MergeFieldApplier[] = [
  (nextKyc, updates) =>
    applyMergeField(
      updates.street,
      (value) => {
        if (nextKyc.addresses) {
          nextKyc.addresses.street = value as string | null;
        }
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.postalAddress,
      (value) => {
        if (nextKyc.addresses) {
          nextKyc.addresses.postalAddress = value as string | null;
        }
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.postalCode,
      (value) => {
        if (nextKyc.addresses) {
          nextKyc.addresses.postalCode = value as string | null;
        }
      },
      (value) => value ?? null,
    ),
  (nextKyc, updates) =>
    applyMergeField(
      updates.city,
      (value) => {
        if (nextKyc.addresses) {
          nextKyc.addresses.city = value as string | null;
        }
      },
      (value) => value ?? null,
    ),
];

const DEFAULT_PROFESSION_AND_INCOMES = {
  id: "",
  employmentType: { id: 0, code: "", label: "" },
  educationLevel: { id: 0, code: "", label: "" },
  structureLevel: { id: 0, code: "", label: "" },
  profession: "",
  company: "",
  employerEmail: "",
  hireDate: "",
  monthlyIncome: 0,
  additionalIncome: 0,
  incomeSource: "",
};

function mergeSimpleFields(
  nextKyc: CustomerKYCData,
  updates: Partial<CustomerKYCUpdateDTO>,
): void {
  for (const applyField of DIRECT_MERGE_FIELDS) {
    applyField(nextKyc, updates);
  }

  for (const applyField of NULL_FALLBACK_MERGE_FIELDS) {
    applyField(nextKyc, updates);
  }

  applyMergeField(
    updates.pep,
    (value) => {
      nextKyc.pep = value as boolean;
    },
    (value) => value ?? false,
  );
}

function mergeFamilyStatus(
  nextKyc: CustomerKYCData,
  updates: Partial<CustomerKYCUpdateDTO>,
): void {
  if (updates.familyStatusCode === undefined) {
    return;
  }

  nextKyc.familyStatus = {
    ...(nextKyc.familyStatus ?? { id: 0, code: "", label: "" }),
    code: updates.familyStatusCode ?? "",
    label: updates.familyStatusName ?? nextKyc.familyStatus?.label ?? "",
  };
}

function mergeEmployer(
  nextKyc: CustomerKYCData,
  updates: Partial<CustomerKYCUpdateDTO>,
): void {
  if (updates.employer === undefined) {
    return;
  }

  nextKyc.professionAndIncomes = {
    ...(nextKyc.professionAndIncomes ?? DEFAULT_PROFESSION_AND_INCOMES),
    company: updates.employer ?? "",
  };
}

function mergeProfessionCountry(
  nextKyc: CustomerKYCData,
  updates: Partial<CustomerKYCUpdateDTO>,
): void {
  if (updates.professionCountryCode === undefined) {
    return;
  }

  nextKyc.professionCountry = {
    code: updates.professionCountryCode ?? "",
    name: updates.professionCountryName ?? nextKyc.professionCountry?.name ?? "",
  };
}

function mergeAddress(
  nextKyc: CustomerKYCData,
  updates: Partial<CustomerKYCUpdateDTO>,
): void {
  const hasAddressUpdate =
    updates.street !== undefined ||
    updates.postalAddress !== undefined ||
    updates.postalCode !== undefined ||
    updates.city !== undefined ||
    updates.countryId !== undefined;

  if (!nextKyc.addresses || !hasAddressUpdate) {
    return;
  }

  nextKyc.addresses = { ...nextKyc.addresses };
  for (const applyAddressField of ADDRESS_NULL_FALLBACK_FIELDS) {
    applyAddressField(nextKyc, updates);
  }

  if (updates.countryId !== undefined) {
    const code = updates.countryId;
    const label = updates.countryName ?? nextKyc.addresses.country?.label ?? "";
    nextKyc.addresses.country = {
      id: code,
      code,
      label,
    };
  }
}

function mergeCountryResidence(
  nextKyc: CustomerKYCData,
  updates: Partial<CustomerKYCUpdateDTO>,
): void {
  if (updates.countryId === undefined) {
    return;
  }

  const name = updates.countryName ?? nextKyc.addresses?.country?.label ?? "";
  nextKyc.countryResidence = { code: updates.countryId, name };
}

/**
 * Type guard to check if an error is an Exception
 */
export function isException(error: unknown): error is Exception {
  return (
    error !== null &&
    typeof error === "object" &&
    "type" in error &&
    "message" in error
  );
}

/**
 * Merge a partial CustomerKYCUpdateDTO into an existing CustomerKYCData.
 * Pure helper to keep the store logic smaller and easier to test.
 */
export function mergeKycDataWithUpdates(
  current: CustomerKYCData,
  updates: Partial<CustomerKYCUpdateDTO>,
): CustomerKYCData {
  const nextKyc: CustomerKYCData = { ...current };
  mergeSimpleFields(nextKyc, updates);
  mergeFamilyStatus(nextKyc, updates);
  mergeEmployer(nextKyc, updates);
  mergeProfessionCountry(nextKyc, updates);
  mergeAddress(nextKyc, updates);
  mergeCountryResidence(nextKyc, updates);

  return nextKyc;
}

/**
 * Compute the OTP channel, normalized destination and country code
 * for a given contact field and raw destination.
 */
export function computeOtpDestination(
  field: ContactField,
  kycData: CustomerKYCData | null | undefined,
  destination: string,
): {
  channel: "SMS" | "EMAIL";
  localDestination: string;
  countryCode: string;
} {
  const isPhone = field.startsWith("phone");

  if (!isPhone) {
    return {
      channel: "EMAIL",
      localDestination: destination,
      countryCode: "",
    };
  }

  let countryCode = "";
  if (field === "phone1") {
    countryCode = kycData?.phone1CountryCode ?? "+225";
  } else if (field === "phone2") {
    countryCode = kycData?.phone2CountryCode ?? "+225";
  } else if (field === "phone3") {
    countryCode = kycData?.phone3CountryCode ?? "+225";
  } else if (field === "phone4") {
    countryCode = kycData?.phone4CountryCode ?? "+225";
  }

  let localDestination = destination;
  if (countryCode && destination.startsWith(countryCode)) {
    localDestination = destination.slice(countryCode.length);
  }

  return {
    channel: "SMS",
    localDestination,
    countryCode,
  };
}

/**
 * Translate a raw error thrown by a use case into a displayable Exception,
 * mirroring the handling previously done by the customer view models.
 */
export function handleFetchException(
  error: unknown,
  translate: (key: StringsKeys) => string,
): Exception {
  return ExceptionService.handleException<undefined, DefaultExceptionTypes>(
    error as Exception<undefined, DefaultExceptionTypes>,
    translate,
    (exceptionType: DefaultExceptionTypes) => {
      if (exceptionType === DefaultExceptionTypes.NETWORK_ERROR) {
        return translate(StringsKeys.networkError);
      }
      return null;
    },
  );
}

/**
 * Helper to create a fetch action with proper error handling.
 * Reduces duplication and ensures consistent error handling across slices.
 */
export function createFetchAction<T>(
  set: (partial: Partial<FicheClient360State>) => void,
  loadingKey: keyof FicheClient360State,
  errorKey: keyof FicheClient360State,
  dataKey: keyof FicheClient360State,
  fetchFn: (businessKey: string) => Promise<FetchResult<T>>,
  errorMessage: string,
  getDefaultData: () => T,
): (
  translate: (key: StringsKeys) => string,
  businessKey: string,
) => Promise<void> {
  return async (translate, businessKey) => {
    try {
      set({
        [loadingKey]: true,
        [errorKey]: null,
      } as Partial<FicheClient360State>);

      const response = await fetchFn(businessKey);
      const data = response.data ?? getDefaultData();

      set({
        [dataKey]: data,
        [errorKey]: null,
      } as Partial<FicheClient360State>);
    } catch (error) {
      const exception = handleFetchException(error, translate);

      console.error(errorMessage, exception);

      set({
        [dataKey]: getDefaultData(),
        [errorKey]: exception,
      } as Partial<FicheClient360State>);
    } finally {
      set({
        [loadingKey]: false,
      } as Partial<FicheClient360State>);
    }
  };
}
